import React from 'react';
import AppDropdown from '../AppDropdown';
import AppToggle from '../AppToggle';

export default function AccountDetailsCard({ 
  value = {}, 
  onChange, 
  groupOptions = [], 
  agentOptions = [],
  onAddPlaceClick,
  groupLabel = 'A/c Group',
  budgetLabel = 'Budget',
  agentLabel = 'Agent'
}) {
  const patch = (field, val) => {
    if (onChange) {
      onChange({ ...value, [field]: val });
    }
  };

  const defaultGroups = [
    { value: 'Sundry Creditors', label: 'Sundry Creditors' },
    { value: 'Sundry Debtors', label: 'Sundry Debtors' },
    { value: 'Bank Accounts', label: 'Bank Accounts' },
    { value: 'Current Liabilities', label: 'Current Liabilities' }
  ];

  const defaultAgents = [
    { value: 'Select', label: 'Select' },
    { value: 'Agent-01', label: 'Agent-01' },
    { value: 'Agent-02', label: 'Agent-02' }
  ];

  const resolvedGroupOptions = groupOptions.length > 0 ? groupOptions : defaultGroups;
  const resolvedAgentOptions = agentOptions.length > 0 ? agentOptions : defaultAgents;

  return (
    <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden shadow-xs">
      {/* Header */}
      <div className="bg-surface-container-low/60 px-6 py-3 border-b border-outline-variant/25 text-sm font-bold text-on-surface">
        A/c Details
      </div>

      {/* Inputs */}
      <div className="p-6 flex flex-col gap-4">
        
        {/* A/c Group */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
            {groupLabel}<span className="text-error"> *</span>
          </span>
          <AppDropdown
            value={value.acGroup || resolvedGroupOptions[0]?.value}
            onChange={(val) => patch('acGroup', val)}
            options={resolvedGroupOptions}
            className="col-span-9"
            triggerClassName="h-10 bg-surface-container-low"
          />
        </div>

        {/* A/c Name */}
        <label className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
            A/c Name<span className="text-error"> *</span>
          </span>
          <input
            type="text"
            value={value.acName || ''}
            onChange={(e) => patch('acName', e.target.value)}
            placeholder="Account Name..."
            required
            className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        </label>

        {/* Alias & Place (Inline) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Alias</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              value={value.alias || ''}
              onChange={(e) => patch('alias', e.target.value)}
              placeholder="Alias..."
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">Place</span>
              <div className="flex items-center w-full rounded-xl border border-outline-variant/30 bg-surface-container-low overflow-hidden">
                <input
                  type="text"
                  value={value.place || ''}
                  onChange={(e) => patch('place', e.target.value)}
                  placeholder="Place..."
                  className="h-10 w-full bg-transparent px-4 text-sm text-on-surface outline-none border-0"
                />
                <button
                  type="button"
                  onClick={onAddPlaceClick || (() => patch('place', ''))}
                  className="h-10 px-3 bg-surface-container-high hover:bg-outline-variant/40 border-l border-outline-variant/20 flex items-center justify-center text-primary cursor-pointer transition select-none"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CostCenter & Budget (Inline) */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">CostCenter</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center h-10 select-none">
              <AppToggle
                checked={value.costCenter || false}
                onChange={(val) => patch('costCenter', val)}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">{budgetLabel}</span>
              <input
                type="number"
                value={value.budget ?? 0}
                onChange={(e) => patch('budget', parseInt(e.target.value) || 0)}
                placeholder="0"
                className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
            </div>
          </div>
        </div>

        {/* HSN/SAC & Agent */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">HSN/SAC</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              value={value.hsnSac || ''}
              onChange={(e) => patch('hsnSac', e.target.value)}
              placeholder="HSN/SAC..."
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">{agentLabel}</span>
              <AppDropdown
                value={value.agent || 'Select'}
                onChange={(val) => patch('agent', val)}
                options={resolvedAgentOptions}
                className="w-full"
                triggerClassName="h-10 bg-surface-container-low"
              />
            </div>
          </div>
        </div>

        {/* Remarks Text Area */}
        <label className="grid grid-cols-12 gap-4 items-start">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">Remarks</span>
          <textarea
            value={value.remarks || ''}
            onChange={(e) => patch('remarks', e.target.value)}
            placeholder="Account remarks..."
            rows={2}
            className="col-span-9 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
          />
        </label>

      </div>
    </div>
  );
}
